import { Injectable, inject } from '@angular/core';
import { MatSnackBar, type MatSnackBarRef, type SimpleSnackBar } from '@angular/material/snack-bar';
import { SnackbarMessageWithListComponent } from '../components/snackbar/snackbar-message-with-list/snackbar-message-with-list.component';
import type { SnackBarListMessage } from '../models/snackbar.interface';

@Injectable({ providedIn: 'root' })
export class SnackbarService {
  private snackBar = inject(MatSnackBar);

  private _openSnack: MatSnackBarRef<SimpleSnackBar | SnackbarMessageWithListComponent> | undefined;

  success(message: string) {
    this.snackBar.open(message, undefined, {
      duration: 3000,
      panelClass: ['success'],
    });
  }

  error(message: string) {
    this._openSnack = this.snackBar.open(message, 'DISMISS', {
      panelClass: ['error'],
    });
    this._openSnack.onAction().subscribe(() => {
      this._openSnack?.dismiss();
    });
  }

  // appendNewMessageObjs only works if there is no possibility of the user dismissing the first snackbar message before the second is called
  errorWithList(config: { messageObjs: SnackBarListMessage[]; appendNewMessageObjs: boolean }) {
    const messageObjs: SnackBarListMessage[] = config.messageObjs;
    const currentMessageObjs: SnackBarListMessage[] =
      this._openSnack?.containerInstance.snackBarConfig.data.messageObjs;

    if (config.appendNewMessageObjs && currentMessageObjs) {
      // Append existing deduped messageObjs
      messageObjs.push(
        ...currentMessageObjs.filter(
          (currentMessageObjs) => !messageObjs.find((newMessage) => newMessage.message === currentMessageObjs.message)
        )
      );
    }

    this._openSnack = this.snackBar.openFromComponent(SnackbarMessageWithListComponent, {
      data: { messageObjs },
      panelClass: ['error'],
    });
  }

  errorWithHandler(rawError: unknown, errorPrefix: string, optGenericMessage?: string) {
    // ex. error with unhandled error type:   snackbarService.errorWithHandler(err, 'Error Uploading Document', 'Please Try Again') =>  'Error Uploading Document: Please Try Again'
    const detail = optGenericMessage || this._resolveApiMessage(rawError);
    const displayMessage = detail ? `${errorPrefix}: ${detail}` : errorPrefix;

    this._openSnack = this.snackBar.open(displayMessage, 'DISMISS', {
      panelClass: ['error'],
    });
    this._openSnack.onAction().subscribe(() => {
      this._openSnack?.dismiss();
    });
  }

  /**
   * Pull an API-provided message off a raw error, discarding Angular's default
   * HttpErrorResponse text. The HttpErrorInterceptor sets `message` to the
   * response body's `message` when present, but falls back to Angular's default
   * ("Http failure response for ...") when the body has none — that raw text
   * must never reach the user. Returns '' when there is no usable API message.
   */

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  private _resolveApiMessage(rawError: any): string {
    const message = typeof rawError?.message === 'string' ? rawError.message.trim() : '';
    const isDefaultHttpMessage = /^Http failure (response|during parsing) for /.test(message);
    return isDefaultHttpMessage ? '' : message;
  }

  dismiss() {
    this._openSnack?.dismiss();
    // free up memory
    this._openSnack = undefined;
  }
}
